<template>
  <form class="form-group" @submit.prevent="handleSubmit" ref="formRef">
    <label for="thumbnailFile">📷 Photo du véhicule</label>
    <input
      type="file"
      id="thumbnailFile"
      accept="image/*"
      @change="handleFileChange"
    />
    <!-- Prévisualisation : image existante OU nouvelle sélection -->
    <div v-if="thumbnailPreview || existingThumbnailUrl" class="preview">
      <img
        :src="thumbnailPreview || existingThumbnailUrl"
        alt="Aperçu"
      />
      <div class="preview-actions">
        <button
          v-if="thumbnailPreview"
          type="button"
          @click="removeFile"
          class="btn btn-secondary"
        >
          Supprimer la nouvelle photo
        </button>
        <span v-if="existingThumbnailUrl && !thumbnailPreview" class="preview-label">
          Photo actuelle
        </span>
        <span v-if="thumbnailPreview" class="preview-label">
          Nouvelle photo (remplacera l'ancienne)
        </span>
      </div>
    </div>

    <label for="immatriculation">Numéro d'immatriculation *</label>
    <input
      v-model="localData.noImmat"
      type="text"
      placeholder="Ex: AB-123-CD"
      id="immatriculation"
      required
    />
    <div class="group-rows">
      <div class="group-columns">
        <label for="nbPlace">Nombre de places *</label>
        <input
          v-model.number="localData.nbPlaces"
          id="nbPlace"
          type="number"
          placeholder="Ex: 50"
          min="1"
          required
        />
      </div>
      <div class="group-columns">
        <label for="nbBags">Nombre de bagages autorisés</label>
        <input
          v-model.number="localData.nbBags"
          id="nbBags"
          type="number"
          placeholder="Ex: 2"
          min="0"
        />
      </div>

    </div>

    <label for="company">Compagnie *</label>
    <select v-model="localData.companyId" id="company" required>
      <option value="">-- Sélectionner une compagnie --</option>
      <option v-for="company in companies" :key="company.id" :value="company.id">
        {{ company.name }}
      </option>
    </select>

    <fieldset>
      <legend>Fonctionnalités</legend>
      <div class="checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="localData.airConditioning" />
          <span>❄️ Climatisation</span>
        </label>

        <label class="checkbox-label">
          <input type="checkbox" v-model="localData.toilet" />
          <span>🚽 Toilettes</span>
        </label>

        <label class="checkbox-label">
          <input type="checkbox" v-model="localData.tv" />
          <span>📺 TV</span>
        </label>
      </div>
    </fieldset>
    <div v-if="error" class="error-message">{{ error }}</div>
  </form>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { config } from '@/config/env.js'

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({
      noImmat: '',
      model: '',
      nbPlaces: null,
      companyId: '',
      airConditioning: false,
      nbBags: 0,
      toilet: false,
      tv: false,
      thumbnailName: ''
    })
  },
  companies: { type: Array, default: () => [] }
})

const emit = defineEmits(['submit', 'cancel'])

const formRef = ref(null)
const error = ref('')
const thumbnailPreview = ref(null)  // Preview du nouveau fichier sélectionné
const existingThumbnailUrl = ref(null)  // URL de l'image existante

const localData = ref({
  noImmat: props.formData?.noImmat || '',
  model: props.formData?.model || '',
  nbPlaces: props.formData?.nbPlaces || null,
  companyId: props.formData?.companyId || '',
  airConditioning: props.formData?.airConditioning || false,
  nbBags: props.formData?.nbBags || 0,
  toilet: props.formData?.toilet || false,
  tv: props.formData?.tv || false,
  thumbnailFile: null
})

// Charger l'image existante si elle existe
onMounted(() => {
  if (props.formData?.thumbnailName) {
    // Construire l'URL vers l'image existante
    existingThumbnailUrl.value = `${config.apiUrl.replace('/api', '')}/uploads/vehicles/${props.formData.thumbnailName}`
  }
})

watch(() => props.formData, (newFormData) => {
  if (newFormData) {
    localData.value = {
      noImmat: newFormData.noImmat || '',
      model: newFormData.model || '',
      nbPlaces: newFormData.nbPlaces || null,
      companyId: newFormData.companyId || '',
      airConditioning: newFormData.airConditioning || false,
      nbBags: newFormData.nbBags || 0,
      toilet: newFormData.toilet || false,
      tv: newFormData.tv || false,
      thumbnailFile: null
    }

    // Mettre à jour l'URL de l'image existante
    if (newFormData.thumbnailName) {
      existingThumbnailUrl.value = `${config.apiUrl.replace('/api', '')}/uploads/vehicles/${newFormData.thumbnailName}`
    } else {
      existingThumbnailUrl.value = null
    }

    // Réinitialiser la preview du nouveau fichier
    thumbnailPreview.value = null
  }
}, { deep: true })

function handleFileChange(event) {
  const file = event.target.files[0]

  if (!file) {
    localData.value.thumbnailFile = null
    thumbnailPreview.value = null
    return
  }

  // Validation : taille max 5MB
  if (file.size > 5 * 1024 * 1024) {
    error.value = 'Le fichier est trop volumineux (max 5MB)'
    event.target.value = ''
    return
  }

  // Validation : type image uniquement
  if (!file.type.startsWith('image/')) {
    error.value = 'Seules les images sont acceptées'
    event.target.value = ''
    return
  }

  // Stocker le fichier
  localData.value.thumbnailFile = file
  error.value = ''

  // Créer une prévisualisation du nouveau fichier
  const reader = new FileReader()
  reader.onload = (e) => {
    thumbnailPreview.value = e.target.result
  }
  reader.readAsDataURL(file)
}

// Supprimer le nouveau fichier sélectionné
function removeFile() {
  localData.value.thumbnailFile = null
  thumbnailPreview.value = null
  const fileInput = document.getElementById('thumbnailFile')
  if (fileInput) fileInput.value = ''
}

const handleSubmit = () => {
  error.value = ''

  if (!formRef.value?.checkValidity()) {
    formRef.value?.reportValidity()
    return
  }

  if (localData.value.nbPlaces <= 0) {
    error.value = 'Le nombre de places doit être positif'
    return
  }

  emit('submit', { ...localData.value })
}

defineExpose({ handleSubmit })
</script>
